import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import {
  API_BASE_URL,
  Client,
  CreateBreakInOutCommand,
  FileParameter,
  OnePBreakInOutCreateDTO,
  OnePInitiativeDTO,
  OnePPInitiativeActualCostTotalsFlagsDTO,
  OnePProjectCostTotalsDTO,
  OnePProjectCostTotalsWithActualsFlagDTO,
} from '../../../api/api-clientInformation';
import { ToastrService } from 'ngx-toastr';
import { CostTotalsService } from '../../../services/cost-totals.service';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '../loader/loader.service';

interface IChangeSet {
  changeSetId: number;
}
@Component({
  selector: 'app-ript-settings-dialog',
  standalone: false,
  templateUrl: './new-break-in-out-dialog.component.html',
  styleUrl: './new-break-in-out-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewBreakInOutDialogComponent implements OnInit, OnDestroy {
  apiClient = inject(Client);
  private readonly cdr = inject(ChangeDetectorRef);
  // The root instance is the one the interceptor and app.component use; lazy modules provide their own copies.
  private readonly loader = inject(ApplicationRef).injector.get(LoaderService);
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL, { optional: true }) ?? '';
  initiativeId = 0;
  delta = 0;
  revisedOutlook = 0;
  selectedFile?: File;
  saving = false;
  readonly maxJustificationLength = 4000;
  public breakInOutForm!: FormGroup;
  private initiativeCostTotals = {} as OnePProjectCostTotalsDTO;
  private _initiativeCostTotalsPayload = {} as OnePProjectCostTotalsWithActualsFlagDTO;
  private _fb: FormBuilder = new FormBuilder();
  private changeSet: any;

  constructor(
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private costTotalsService: CostTotalsService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<NewBreakInOutDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this._fb = fb;
    this.breakInOutForm = this.fb.group({
      amount: [{ value: 0, disabled: true }],
      amountToBeAdded: [{ value: 0, disabled: true }],
      outlook: [{ value: 0, disabled: true }],
      status: [{ value: 'Pending', disabled: true }],
      decision: [{ value: '', disabled: true }],
      project: [{ value: '', disabled: true }],
      justification: [{ value: '', disabled: false }],
    });
  }
  private filterCostTotalsByFlags(
    costTotals: OnePProjectCostTotalsDTO | undefined,
    flags: OnePPInitiativeActualCostTotalsFlagsDTO | undefined
  ): OnePProjectCostTotalsDTO {
    if (!costTotals || !flags) {
      return new OnePProjectCostTotalsDTO();
    }

    const filtered: any = {};
    const costTotalsAny = costTotals as any;
    const flagsAny = flags as any;

    for (const key in costTotalsAny) {
      if (costTotalsAny.hasOwnProperty(key)) {
        filtered[key] = null;
      }
    }

    for (const key in costTotalsAny) {
      if (costTotalsAny.hasOwnProperty(key) && flagsAny[key] === true) {
        filtered[key] = costTotalsAny[key] ?? 0;
      }
    }

    return new OnePProjectCostTotalsDTO(filtered);
  }

  ngOnInit(): void {
    const initiative = this.data.initiative as OnePInitiativeDTO;

    forkJoin({
      initiativeCostTotals: this.apiClient.getInitiativeCostTotals(this.data.initiative.pk, this.data.initiative.year),
      forecastCostTotals: this.apiClient.getForecastCostTotals(this.data.initiative.pk, this.data.initiative.year),
      changeSet: this.http.get<IChangeSet>(
        `${this.apiBaseUrl}/api/OnePForecast/GetPendingBreakInOutChangeSetId?initiativeId=${this.data.initiative.pk}`
      )
    })
      .subscribe({
        next: ({ initiativeCostTotals, forecastCostTotals, changeSet }) => {
          this.initiativeCostTotals = initiativeCostTotals.initiativeCostTotalsAmount!;
          this.costTotalsService.forecastCostTotals = forecastCostTotals;
          this._initiativeCostTotalsPayload = initiativeCostTotals;
          this.changeSet = changeSet.changeSetId;

          // For future-year projects all months are forecast; for current-year projects
          // merge forecast (future months) with filtered actuals (past months).
          const isFutureYear = (this.data.initiative.year ?? 0) > new Date().getFullYear();
          if (isFutureYear) {

            this.revisedOutlook = this.costTotalsService.calculateTotals(forecastCostTotals);
          } else {
            // Filter out manually-entered actuals and calculate the revised outlook
            const costTotals = this.filterCostTotalsByFlags(
              initiativeCostTotals.initiativeCostTotalsAmount,
              initiativeCostTotals.initiativeActualsFlags
            ) ?? ({} as OnePProjectCostTotalsDTO);
            this.revisedOutlook = this.costTotalsService.calculateTotals(this.costTotalsService.mergeCostTotal(forecastCostTotals, costTotals));
          }
          // Positive delta = current fyGrandTotal is above last month's (Break In); negative = below (Break Out).
          // this.delta = this.data.fyGrandTotal - this.data.lastmonthfygrandtotal;
          // this.delta = this.data.lastmonthfygrandtotal - this.data.fyGrandTotal

          const newvalue = (this.revisedOutlook ?? 0) + (this.data.parentCarriedOverFyOutlook ?? 0);
          const oldvalue = ((initiative.fyOutlook ?? 0) + (this.data.parentCarriedOverFyOutlook ?? 0));
          const lastmonthfygrandtotal = (this.data.lastmonthfygrandtotal ?? 0);
          // this.delta = Math.round(((this.revisedOutlook ?? 0) - (initiative.fyOutlook ?? 0)) * 100) / 100;
          this.delta = Math.round(((newvalue ?? 0) - (lastmonthfygrandtotal ?? 0)) * 100) / 100;
        
          // this.deltaNew = Math.round(((newvalue ?? 0) - (lastmonthfygrandtotal ?? 0)) * 100) / 100;
          // Guard: a Break In/Out with no delta change has no approval value
          if (this.delta === 0) {
            this.toastrService.warning(this.translate.instant('MESSAGES.ERROR.NO_FORECAST_CHANGES'));
            this.dialogRef.close();
            return;
          }


          this.breakInOutForm.get('amount')!.setValue(this.delta);
          this.breakInOutForm
            .get('amountToBeAdded')!
            .setValue(Math.round(((this.revisedOutlook ?? 0) - (initiative.fyOutlook ?? 0)) * 100) / 100);
          this.breakInOutForm.get('outlook')!.setValue(initiative.fyOutlook);
          this.breakInOutForm.get('project')!.setValue(initiative.id);
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('MESSAGES.ERROR.CANNOT_REQUEST_TOTALS'));
          return;
        }
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const maxSizeInMB = 50;
    if (file.size > maxSizeInMB * 1024 * 1024) {
      this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FILE_SIZE_EXCEEDS', { maxSize: maxSizeInMB }));
      input.value = '';
      return;
    }

    this.selectedFile = file;
    input.value = '';
    this.cdr.markForCheck();
  }

  onRemoveFile(): void {
    this.selectedFile = undefined;
    this.cdr.markForCheck();
  }

  // The break-in/out is already saved at this point, so an upload failure is reported but never blocks closing.
  private uploadAttachment(breakInOutId: number): void {
    if (!this.selectedFile) {
      this.dialogRef.close(true);
      return;
    }
    const fileParam: FileParameter = { data: this.selectedFile, fileName: this.selectedFile.name };
    this.apiClient.uploadFileAttachment(this.data.initiative.pk, breakInOutId, fileParam).subscribe({
      next: () => this.dialogRef.close(true),
      error: () => {
        this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FILE_UPLOAD_FAILED'));
        this.dialogRef.close(true);
      },
    });
  }

  private setSaving(value: boolean): void {
    this.saving = value;
    this.loader.setSuppressed(value);
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.loader.setSuppressed(false);
  }

  onSave() {
    if (this.saving) return;
    const initiative = this.data.initiative as OnePInitiativeDTO;
    const newBreakInOut = {} as OnePBreakInOutCreateDTO;
    newBreakInOut.initiativeFk = this.data.initiative.pk;
    this.breakInOutForm.markAllAsTouched();
    if (!this.breakInOutForm.valid)
      return;
    const values = this.breakInOutForm.getRawValue();
    Object.assign(newBreakInOut, values);
    newBreakInOut.amount = this.delta;
    newBreakInOut.revisedOutlook = this.revisedOutlook;
    newBreakInOut.forecastCostTotalChangeSetId = this.changeSet ?? undefined;
    newBreakInOut.lastFyGrandTotal = (this.data.lastmonthfygrandtotal ?? 0);
    newBreakInOut.currentFyGrandTotal = ((initiative.fyOutlook ?? 0) + (this.data.parentCarriedOverFyOutlook ?? 0));
    //newBreakInOut.justification
    this.setSaving(true);
    this.apiClient
      .createBreakInOut({
        onePBreakInOutCreateDTO: newBreakInOut,
      } as CreateBreakInOutCommand)
      .subscribe({
        next: (pk) => {
          let _costTotals: OnePProjectCostTotalsDTO = {} as OnePProjectCostTotalsDTO;
          const isFutureYear = (this.data.initiative.year ?? 0) > new Date().getFullYear();
          if (isFutureYear) {
            _costTotals = this.costTotalsService.forecastCostTotals;
          }
          else {
            const costTotals = this.filterCostTotalsByFlags(
              this._initiativeCostTotalsPayload.initiativeCostTotalsAmount,
              this._initiativeCostTotalsPayload.initiativeActualsFlags
            ) ?? ({} as OnePProjectCostTotalsDTO);

            _costTotals = this.costTotalsService.mergeCostTotal(this.costTotalsService.forecastCostTotals, costTotals);
          }
          this.apiClient.upsertBreakInOutCostTotals(pk, _costTotals)
            .subscribe({
              next: () => {
                this.toastrService.success(
                  this.translate.instant('MESSAGES.SUCCESS.BREAK_IN_OUT_SAVED')
                );

                this.uploadAttachment(pk);
              },
              error: () => {
                this.setSaving(false);
                this.toastrService.error(this.translate.instant('MESSAGES.ERROR.BREAK_IN_OUT_TOTALS_NOT_SAVED'));
              }
            });
        },
        error: (err) => {
          this.setSaving(false);
          if (err) {
            const errorBody = JSON.parse(err.response);
            if (errorBody.Error) {
              this.toastrService.error(errorBody.Error);
            } else if (errorBody.Errors) {
              this.toastrService.error(errorBody.Errors[0].ErrorMessage);
            } else {
              this.toastrService.error(this.translate.instant('MESSAGES.ERROR.SOMETHING_WENT_WRONG'));
            }
          } else {
            this.toastrService.error(this.translate.instant('MESSAGES.ERROR.BREAK_IN_OUT_NOT_SAVED'));
          }
          return false;
        },
      });
  }
}
